import { Sounds } from '@/constants/Sounds.ts';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import { IBrowserNotificationPayload } from '@/interfaces/powerMarkets/notifications/IBrowserNotificationPayload.ts';
import { ISubscriptionSettings } from '@/interfaces/powerMarkets/notifications/ISubscriptionSettings.ts';

export const BROWSER_SOUND_PREVIEW_VOICE_TEXT = 'System Price, 14:30, 82.50 pounds per megawatt hour.';

export const BROWSER_SOUND_OPTIONS: Array<IIdName> = [
    { id: 'None', name: 'No sound' },
    { id: 'voice', name: 'Spoken voice' },
    ...Sounds
        .filter((soundItem) => soundItem.name !== 'None')
        .map((soundItem) => ({ id: soundItem.name, name: soundItem.name })),
];

export function findToneByChoice (soundChoice: string): { name: string; sound: HTMLAudioElement | null } | undefined {
    return Sounds.find((soundItem) => soundItem.name === soundChoice);
}

export function playAudioSample (sound: HTMLAudioElement | null): boolean {
    if (!sound) return false;

    try {
        sound.currentTime = 0;
        const promise = sound.play();
        if (promise !== undefined) {
            promise.catch(() => undefined);
        }
        return true;
    } catch {
        return false;
    }
}

export function speakNotificationText (text: string): boolean {
    if (!text || !text.trim()) return false;
    if (typeof window === 'undefined' || !window.speechSynthesis) return false;
    if (typeof SpeechSynthesisUtterance === 'undefined') return false;

    try {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.pitch = 1;
        utterance.rate = 1.3;
        utterance.volume = 1;

        const voices = window.speechSynthesis.getVoices();
        const language = (window.navigator.language || 'en-GB').toLowerCase();
        const languagePrefix = language.split('-')[0];
        const preferredVoice = voices.find((voice) => voice.lang.toLowerCase() === language)
            || voices.find((voice) => voice.lang.toLowerCase().startsWith(`${languagePrefix}-`) && voice.default)
            || voices.find((voice) => voice.lang.toLowerCase().startsWith(`${languagePrefix}-`))
            || voices.find((voice) => voice.default)
            || voices[0];
        if (preferredVoice) {
            utterance.voice = preferredVoice;
        }

        window.speechSynthesis.speak(utterance);
        return true;
    } catch {
        return false;
    }
}

export function resolveBrowserSoundChoice (
    alert: Pick<IBrowserNotificationPayload, 'browserSound' | 'browserSoundChoice'>,
): string {
    if (alert.browserSoundChoice != null && alert.browserSoundChoice !== '') {
        return alert.browserSoundChoice;
    }
    return alert.browserSound ? 'voice' : 'None';
}

export function resolveSubscriptionBrowserSoundChoice (
    settings: Pick<ISubscriptionSettings, 'browserSound' | 'browserSoundChoice'>,
): string {
    if (settings.browserSoundChoice != null && settings.browserSoundChoice !== '') {
        return settings.browserSoundChoice;
    }
    return settings.browserSound === 1 ? 'voice' : 'None';
}

export function isBrowserSoundMethodSelected (choice: string): boolean {
    return choice !== 'None';
}

export function previewBrowserSoundChoice (choice: string): void {
    if (choice === 'None') return;
    if (choice === 'voice') {
        speakNotificationText(BROWSER_SOUND_PREVIEW_VOICE_TEXT);
        return;
    }
    const tone = findToneByChoice(choice);
    playAudioSample(tone?.sound ?? null);
}

export function playBrowserNotificationSound (soundChoice: string, fallbackText: string): boolean {
    const tone = findToneByChoice(soundChoice);
    if (!tone?.sound) return false;

    try {
        tone.sound.currentTime = 0;
        const promise = tone.sound.play();
        if (promise !== undefined) {
            promise.catch(() => speakNotificationText(fallbackText));
        }
        return true;
    } catch {
        return false;
    }
}

export function handleBrowserNotificationSound (alert: IBrowserNotificationPayload): void {
    if (!alert.browserSound) return;

    const choice = resolveBrowserSoundChoice(alert);
    if (choice === 'None') return;

    const soundText = alert.data?.browserSoundNotificationText ?? '';
    if (choice === 'voice' || !playBrowserNotificationSound(choice, soundText)) {
        speakNotificationText(soundText);
    }
}
